// /campaigns/new and /campaigns/:campaignId/edit.
// Hosts the 5-step wizard with draft autosave, per-step validation, stepper,
// footer and the summary aside. Header is inline in the first PageSection so
// it scrolls with the content.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { enqueueSnackbar } from 'notistack';
import { Page, Typo, SimpleButton, Modal } from '../../../ui';
import PageHeader from '../../../ui/Page/PageHeader';
import PageSection from '../../../ui/Page/PageSection';
import { media } from '../../../utils/media';
import { TOAST_SUCCESS, TOAST_ERROR } from '../../../utils/constants';
import { useUser } from '../../auth/hooks';

import WizardStepper from '../components/wizard/WizardStepper';
import WizardFooter from '../components/wizard/WizardFooter';
import WizardSummaryAside from '../components/wizard/WizardSummaryAside';
import StepBasics from '../components/wizard/StepBasics';
import StepAudience from '../components/wizard/StepAudience';
import StepContent from '../components/wizard/StepContent';
import StepSchedule from '../components/wizard/StepSchedule';
import StepReview, { TestSendDialog } from '../components/wizard/StepReview';

import { useCampaignDraft } from '../hooks/useCampaignDraft';
import { useCampaignPermissions } from '../hooks/useCampaignPermissions';
import { useCampaignsActions } from '../hooks/useCampaignsActions';
import {
  selectWizardDirty,
  selectWizardLastSavedAt,
  selectWizardMaxReached,
  selectWizardStep,
  setWizardMaxReached,
  setWizardStep,
  resetWizard,
} from '../campaignsSlice';
import { maxReachableStep, validateStep } from '../utils/campaignValidation';
import { WIZARD_STEP } from '../constants';

const Breadcrumb = styled.div`
  color: ${({ theme }) => theme.colors.NEU_600};
  font-size: 13px;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const SavedHint = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.NEU_600};
`;

const SubHeading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Body = styled.div`
  display: flex;
  gap: 20px;
  width: 100%;
  align-items: flex-start;

  /* Stack at laptop — below that the 320px aside crowds the wizard step body. */
  ${media.lessThan('laptop')} {
    flex-direction: column;
  }
`;

const Main = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

const Side = styled.div`
  flex: 0 0 320px;
  width: 100%;

  ${media.lessThan('laptop')} {
    flex: 1 1 auto;
  }
`;

const stepName = (step) => {
  switch (step) {
    case WIZARD_STEP.BASICS:
      return 'Basics';
    case WIZARD_STEP.AUDIENCE:
      return 'Build audience';
    case WIZARD_STEP.CONTENT:
      return 'Create content';
    case WIZARD_STEP.SCHEDULE:
      return 'Schedule';
    case WIZARD_STEP.REVIEW:
      return 'Review';
    default:
      return '';
  }
};

const lastSavedLabel = (iso) => {
  if (!iso) return '';
  const seconds = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return `Saved ${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  return `Saved ${minutes}m ago`;
};

const CampaignWizardPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { campaignId } = useParams();
  const permissions = useCampaignPermissions();
  const user = useUser();

  const step = useSelector(selectWizardStep);
  const maxReached = useSelector(selectWizardMaxReached);
  const dirty = useSelector(selectWizardDirty);
  const lastSavedAt = useSelector(selectWizardLastSavedAt);

  const { draft, update, saveNow, flushOnBlur, isLoading, isSaving } = useCampaignDraft(campaignId);
  const { sendCampaign, testSend } = useCampaignsActions();

  const [exitPromptOpen, setExitPromptOpen] = useState(false);
  const [testSendOpen, setTestSendOpen] = useState(false);
  const [isLaunching, setIsLaunching] = useState(false);
  const [isSendingTest, setIsSendingTest] = useState(false);

  const currentValidation = useMemo(() => validateStep(step, draft), [step, draft]);
  const canContinue = currentValidation.valid;

  // Steps always pass a full draft (or a functional updater); just hand it through.
  const handleUpdate = update;

  const goToStep = useCallback(
    (next) => {
      dispatch(setWizardStep(next));
      dispatch(setWizardMaxReached(Math.max(maxReached, maxReachableStep(draft))));
    },
    [dispatch, draft, maxReached],
  );

  const handleBack = useCallback(() => {
    if (step > WIZARD_STEP.BASICS) goToStep(step - 1);
  }, [step, goToStep]);

  const handleContinue = useCallback(() => {
    flushOnBlur();
    if (step < WIZARD_STEP.REVIEW) goToStep(step + 1);
  }, [step, flushOnBlur, goToStep]);

  const handleSaveDraft = useCallback(async () => {
    try {
      await saveNow();
      enqueueSnackbar(t('campaigns.draftSaved', 'Draft saved'), { variant: TOAST_SUCCESS });
      navigate('/campaigns');
    } catch {
      enqueueSnackbar(t('campaigns.draftSaveFailed', 'Could not save draft'), { variant: TOAST_ERROR });
    }
  }, [saveNow, navigate, t]);

  const handleExit = useCallback(() => {
    if (dirty) {
      setExitPromptOpen(true);
    } else {
      dispatch(resetWizard());
      navigate('/campaigns');
    }
  }, [dirty, dispatch, navigate]);

  const confirmExitDiscard = useCallback(() => {
    dispatch(resetWizard());
    setExitPromptOpen(false);
    navigate('/campaigns');
  }, [dispatch, navigate]);

  const handleLaunch = useCallback(async () => {
    setIsLaunching(true);
    try {
      const saved = await saveNow();
      if (!saved) return;
      await sendCampaign({ id: saved.id, sendAt: saved.schedule?.email?.sendAt });
      dispatch(resetWizard());
      navigate(`/campaigns/${saved.id}`);
    } catch {
      enqueueSnackbar(t('campaigns.launchFailed', 'Could not launch campaign'), { variant: TOAST_ERROR });
    } finally {
      setIsLaunching(false);
    }
  }, [saveNow, sendCampaign, dispatch, navigate, t]);

  const handleTestSend = useCallback(
    async (recipients) => {
      if (!recipients?.length) return;
      setIsSendingTest(true);
      try {
        const saved = await saveNow();
        const id = saved?.id ?? draft.id;
        if (!id) {
          enqueueSnackbar(t('campaigns.testSendNeedsSave', 'Save the draft before sending a test.'), { variant: TOAST_ERROR });
          return;
        }
        await testSend({ id, recipients });
        setTestSendOpen(false);
      } catch {
        // surfaced via toast
      } finally {
        setIsSendingTest(false);
      }
    },
    [saveNow, testSend, draft, t],
  );

  useEffect(() => {
    if (!permissions.featureEnabled) navigate('/dashboard', { replace: true });
  }, [permissions.featureEnabled, navigate]);

  if (!permissions.featureEnabled) return null;

  if (isLoading) {
    return (
      <Page>
        <PageSection>
          <Typo.MediumText>{t('globals.loading', 'Loading…')}</Typo.MediumText>
        </PageSection>
      </Page>
    );
  }

  const renderStep = () => {
    switch (step) {
      case WIZARD_STEP.BASICS:
        return <StepBasics draft={draft} errors={currentValidation.errors} onChange={handleUpdate} />;
      case WIZARD_STEP.AUDIENCE:
        return <StepAudience draft={draft} onChange={handleUpdate} />;
      case WIZARD_STEP.CONTENT:
        return <StepContent draft={draft} errors={currentValidation.errors} onChange={handleUpdate} />;
      case WIZARD_STEP.SCHEDULE:
        return <StepSchedule draft={draft} errors={currentValidation.errors} onChange={handleUpdate} />;
      case WIZARD_STEP.REVIEW:
        return <StepReview draft={draft} onJumpToStep={goToStep} onTestSendOpen={() => setTestSendOpen(true)} />;
      default:
        return null;
    }
  };

  const headerTitle = (
    <SubHeading>
      <Breadcrumb>
        <button type='button' onClick={handleExit} style={{ background: 'none', border: 0, color: 'inherit', padding: 0, cursor: 'pointer', font: 'inherit' }}>
          {t('campaigns.title', 'Campaigns')}
        </button>
        {' › '}
        {campaignId ? draft?.name || t('campaigns.editCampaign', 'Edit campaign') : t('campaigns.newCampaign', 'New campaign')}
      </Breadcrumb>
      <Typo.ContentSmallHeader>
        {campaignId ? t('campaigns.editCampaign', 'Edit campaign') : t('campaigns.createCampaign', 'Create campaign')}
      </Typo.ContentSmallHeader>
      <Typo.SmallText color='textSecondary'>
        {t('campaigns.stepLabel', 'Step {{n}} of 5 — {{label}}', { n: step, label: t(`campaigns.stepHeading.${step}`, stepName(step)) })}
      </Typo.SmallText>
    </SubHeading>
  );

  return (
    <Page gap={20}>
      <PageSection gap={20} align='stretch'>
        <PageHeader title={headerTitle}>
          <HeaderActions>
            {lastSavedAt && <SavedHint>{lastSavedLabel(lastSavedAt)}</SavedHint>}
            <SimpleButton variant='ghost' onClick={handleSaveDraft} isLoading={isSaving}>
              {t('campaigns.saveAsDraft', 'Save as draft')}
            </SimpleButton>
            <SimpleButton variant='ghost' onClick={handleExit}>
              {t('globals.cancel', 'Cancel')}
            </SimpleButton>
          </HeaderActions>
        </PageHeader>
        <WizardStepper step={step} maxReached={Math.max(maxReached, maxReachableStep(draft))} onJump={goToStep} />
        <Body>
          <Main>
            {renderStep()}
            <WizardFooter
              step={step}
              canContinue={canContinue}
              isSaving={isSaving}
              isLaunching={isLaunching}
              onBack={handleBack}
              onSaveDraft={handleSaveDraft}
              onContinue={handleContinue}
              onLaunch={handleLaunch}
              onTestSend={() => setTestSendOpen(true)}
            />
          </Main>
          <Side>
            <WizardSummaryAside draft={draft} />
          </Side>
        </Body>
      </PageSection>

      <Modal
        open={exitPromptOpen}
        onClose={() => setExitPromptOpen(false)}
        title={t('campaigns.exitPromptTitle', 'Unsaved changes')}
        showActions
        onAccept={confirmExitDiscard}
        acceptLabel={t('campaigns.exitDiscard', 'Discard')}
        variant='action'
      >
        <Typo.MediumText>
          {t('campaigns.exitPromptBody', 'You have unsaved changes. Save as draft to keep your progress, or discard to leave without saving.')}
        </Typo.MediumText>
      </Modal>

      <TestSendDialog open={testSendOpen} defaultEmail={user?.email} onClose={() => setTestSendOpen(false)} onSend={handleTestSend} isSending={isSendingTest} />
    </Page>
  );
};

export default CampaignWizardPage;
